import * as Sentry from "@sentry/react";
import { useNavigate } from "@tanstack/react-router";
import { isEmpty } from "lodash";
import { CircleAlert, Pencil, Plus } from "lucide-react";
import React, { useCallback } from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useInterval } from "usehooks-ts";

import { invoiceExtractionRetry } from "@/apis/custom/customRoutes";
import type { LineItem } from "@/apis/gql/types";
import { InvoiceComments } from "@/components/shared/invoice-comments";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usePermissions } from "@/hooks/usePermissions";
import { InvoiceStatus } from "@/lib/constants/invoiceStatus";
import { useInvoiceBrowser } from "@/providers/invoice-browser";
import { useTenant } from "@/providers/session";
import { useDeleteInvoice } from "@/queries/invoices/delete";
import { useRemoveDuplicates } from "@/queries/invoices/remove-duplicates";
import { useUpdateInvoice } from "@/queries/invoices/update";

import type { AlertVariant } from "@/components/shared/alerts/simple-alert";
import { useValidation } from "@/components/shared/form/shared/form-validation";
import { CommentsCount } from "@/components/shared/invoice-comments/comments-count";
import { LedgerEntriesTable } from "@/components/shared/ledger/ledger-entries-table";
import { SuperAdminOnly } from "@/components/shared/miscellaneous/superadmin-only";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/spinner";
import { parseAsBoolean, useQueryState } from "nuqs";
import { AiProcessingCard } from "../components/ai-processing-card";
import { NavigationBar } from "../components/navigation-bar";
import {
  UpdateInvoiceFields,
  useInvoiceReviewContext,
  useInvoiceReviewStateDispatch,
  useSaveInvoice,
} from "./ReviewProvider";
import { InvoiceBookings } from "./components/bookings/invoice-bookings";
import { InvoiceAlert } from "./components/invoice-alert";
import { InvoiceStatusBanner } from "./components/invoice-status-banner";
import { OcrReviewModes } from "./components/ocr-review-mode";
import InvoiceCoreInfoForm, {
  ReviewModes,
} from "./coreInvoiceForm/invoiceCoreInfoForm";
import { InvoiceActivity } from "./invoice-activity";
import { useInvoiceValidation } from "./invoice-validation-provider";
import PaymentDetails from "./paymentDetails/paymentDetails";

const POLLING_INTERVAL = 5000;

function InvoiceReviewForm() {
  const { t } = useTranslation();
  const [reviewMode, setReviewMode] = React.useState(ReviewModes.COMPLETE);
  const [showActivity, setShowActivity] = React.useState(false);
  const { isSuperAdmin } = usePermissions();
  const { isOcrReviewRequired } = useTenant();
  const [ocrReviewMode, setOcrReviewMode] = React.useState(OcrReviewModes.USER);
  const [syncingLineItems, setSyncingLineItems] = React.useState(false);
  const [updateInvoice] = useUpdateInvoice();
  const { companySlug } = useTenant();
  const navigate = useNavigate();
  useEffect(() => {
    if (isSuperAdmin && isOcrReviewRequired) {
      setOcrReviewMode(OcrReviewModes.OCR_REVIEW);
    }
  }, [isSuperAdmin, isOcrReviewRequired]);
  const [isEditing, setIsEditing] = useQueryState(
    "editing",
    parseAsBoolean.withDefault(false).withOptions({
      history: "replace",
      shallow: false,
    }),
  );

  const { invoices: queue, remove: removeInvoiceFromQueue } =
    useInvoiceBrowser();
  const dispatch = useInvoiceReviewStateDispatch();
  const { invoiceReviewState, refetch, loading, saveInvoiceTags } =
    useInvoiceReviewContext();
  const { setInvoiceValidationData } = useInvoiceValidation();
  const [removeDuplicates] = useRemoveDuplicates();
  const deleteInvoices = useDeleteInvoice();
  const { invoiceData } = invoiceReviewState;
  const { errors } = useValidation();
  const { saveInvoice } = useSaveInvoice();

  const { isPaymentEnabled } = useTenant();

  useInterval(
    () => {
      refetch();
    },
    invoiceData?.status === InvoiceStatus.NEW ? POLLING_INTERVAL : null,
  );

  const handleConfirmClick = async () => {
    const invoiceId = invoiceData?.invoiceId;
    const toastId = toast.loading(t("generic_loading"));

    try {
      await saveInvoice(UpdateInvoiceFields.InvoiceStatus, {
        status: InvoiceStatus.REVIEWED,
      });
      toast.dismiss(toastId);
    } catch (e) {
      Sentry.captureException(e);
      navigate({
        to: "/$company/buchhaltung/rechnung/$id",
        params: { company: companySlug, id: invoiceId as string },
      });
      toast.error(t("generic_error"), { richColors: true, id: toastId });
      throw e;
    }
  };

  const handleStatusChange = async (value: InvoiceStatus) => {
    dispatch({
      type: UpdateInvoiceFields.InvoiceStatus,
      payload: value,
    });
    try {
      await saveInvoice(UpdateInvoiceFields.InvoiceStatus, {
        status: value,
      });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const handleRetryExtraction = async () => {
    if (!invoiceData?.invoiceId) return;

    await invoiceExtractionRetry(invoiceData.invoiceId);
    refetch();
  };

  // TODO: move this to a standalone component
  const handleSaveInvoiceTags = async (tags: string[]) => {
    if (!invoiceData?.invoiceId) return;
    toast.promise(saveInvoiceTags([invoiceData.invoiceId], tags), {
      loading: t("generic_loading"),
      success: t("generic_success"),
      error: t("generic_error"),
      finally: () => {
        refetch();
      },
    });
  };

  const handleInvoiceDelete = async (invoiceId: string) => {
    toast.promise(deleteInvoices([invoiceId]), {
      loading: t("generic_loading"),
      success: () => {
        if (queue.length === 0) {
          window.history.back();
        } else {
          removeInvoiceFromQueue();
        }
        return t("generic_success");
      },
      error: t("generic_error"),
    });
  };

  const invoiceInProcessing =
    invoiceData?.status === InvoiceStatus.NEW &&
    invoiceData?.processingStatus &&
    invoiceData?.processingStatus !== "UPLOADED";

  const invoiceInFileProcessing =
    !invoiceData?.processingStatus ||
    invoiceData?.processingStatus === "UPLOADED";

  const handleCreateBooking = async () => {
    if (!invoiceData?.invoiceId) return;

    await updateInvoice({
      invoiceId: invoiceData.invoiceId,
      isBooking: true,
    });
  };

  const handleRemoveBooking = useCallback(async () => {
    if (queue.length === 0) {
      window.history.back();
    } else {
      removeInvoiceFromQueue();
    }
  }, [queue.length, removeInvoiceFromQueue]);

  const handleRemoveDuplicates = useCallback(async () => {
    if (!invoiceData?.invoiceId) return;
    await removeDuplicates({ invoiceIds: [invoiceData?.invoiceId] });
  }, [invoiceData?.invoiceId, removeDuplicates]);

  const handleOnLineItemsChange = useCallback((lineItems: LineItem[]) => {
    setInvoiceValidationData({ lineItems });
  }, []);

  const handleSetSyncingLineItems = useCallback((syncing: boolean) => {
    setSyncingLineItems(syncing);
  }, []);

  useEffect(() => {
    if (isEditing) {
      setIsEditing(null);
    }
  }, [invoiceData?.invoiceId]);

  const FileProcessingCard = () => {
    const { t } = useTranslation();
    return (
      <Alert>
        <CircleAlert className="h-4 w-4" />
        <AlertTitle className="text-lg font-bold">
          {t("file_processing_alert.title")}
        </AlertTitle>
        <AlertDescription>
          {t("file_processing_alert.description")}
        </AlertDescription>
      </Alert>
    );
  };

  const alerts: {
    tagType: "multipleInvoice" | "missingVatId";
    tagDisplayVariant: AlertVariant;
  }[] =
    invoiceData && !invoiceData.isSingleInvoice
      ? [{ tagType: "multipleInvoice", tagDisplayVariant: "destructive" }]
      : [];

  const preparedTags = (invoiceData?.tags ?? [])
    .filter((tag) => tag?.tagName === "Adressat Prüfen")
    .map((tag) => {
      const tagName = tag.tagName as "Adressat Prüfen";
      const base = `invoiceReview.tags.accounting.${tagName}` as const;
      const details = tag.details?.details;
      const mappedDetails = details
        ? {
            [t(
              `${base}.details.companyRecipientNameAnswer` as const,
            ) as string]: {
              flag: details.companyRecipientNameAnswer !== true,
              reasoning: details.companyRecipientNameReasoning,
            },
            [t(
              `${base}.details.companyRecipientAddressAnswer` as const,
            ) as string]: {
              flag: details.companyRecipientAddressAnswer !== true,
              reasoning: details.companyRecipientAddressReasoning,
            },
            [t(
              `${base}.details.companyRecipientVatIdAnswer` as const,
            ) as string]: {
              flag: details.companyRecipientVatIdAnswer !== true,
              reasoning: details.companyRecipientVatIdReasoning,
            },
          }
        : {};
      return {
        tagId: tag.tagId,
        tagName: tag.tagName,
        isApproved: tag.approval === true,
        title: t(`${base}.title` as const) as string,
        message: t(`${base}.message` as const) as string,
        details: mappedDetails,
      };
    });

  // TODO replace this with a proper skeleton
  if (loading)
    return (
      <div className="h-full w-full flex justify-center items-center">
        <LoadingSpinner />
      </div>
    );

  if (isEmpty(invoiceData)) return null;

  return (
    <div className="h-full flex flex-col">
      <div
        className="p-4 flex-1 overflow-auto h-full"
        id="invoice-details-panel"
        style={{ scrollBehavior: "smooth" }}
      >
        <div className="flex flex-col h-full">
          <InvoiceStatusBanner
            invoice={invoiceData}
            ocrReviewMode={ocrReviewMode}
            setOcrReviewMode={setOcrReviewMode}
            onRetryExtraction={handleRetryExtraction}
            onDelete={handleInvoiceDelete}
            onUpdate={handleStatusChange}
            onRemoveBooking={handleRemoveBooking}
            reviewMode={reviewMode}
            onReviewModeChange={setReviewMode}
            showActivity={showActivity}
            setShowActivity={setShowActivity}
          />
          <div className="mt-2" />
          <div className="space-y-3">
            {invoiceInFileProcessing && (
              <div className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-12">
                  <FileProcessingCard />
                </div>
              </div>
            )}
            {!invoiceInFileProcessing &&
              !invoiceInProcessing &&
              !invoiceData.isBooking && (
                <div className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-12">
                    <Alert>
                      <CircleAlert className="h-4 w-4" />
                      <AlertTitle>{t("not_a_booking_alert.title")}</AlertTitle>
                      <AlertDescription>
                        {t("not_a_booking_alert.description")}
                      </AlertDescription>
                      <div className="flex justify-end">
                        <Button
                          variant="outline"
                          className="gap-1"
                          onClick={handleCreateBooking}
                        >
                          <Plus className="h-4 w-4" />
                          {t("create_booking")}
                        </Button>
                      </div>
                    </Alert>
                  </div>
                </div>
              )}
            {invoiceInProcessing && (
              <AiProcessingCard invoiceId={invoiceData.invoiceId} />
            )}
            {invoiceData.isBooking &&
              invoiceData.status !== InvoiceStatus.NEW && (
                <Tabs defaultValue="invoice-data" className="w-full">
                  <TabsList>
                    <TabsTrigger value="invoice-data">
                      {t("invoice_review.invoice_data")}
                    </TabsTrigger>
                    <TabsTrigger
                      value="comments"
                      className="flex items-center gap-2"
                    >
                      {t("invoice_review.comments")}
                      <CommentsCount invoiceId={invoiceData.invoiceId} />
                    </TabsTrigger>
                    <SuperAdminOnly>
                      <TabsTrigger value="ledger">
                        {t("invoice_review.ledger")}
                      </TabsTrigger>
                    </SuperAdminOnly>
                  </TabsList>
                  <TabsContent value="invoice-data">
                    <>
                      <InvoiceAlert
                        tags={preparedTags}
                        alerts={alerts}
                        invoiceId={invoiceData.invoiceId}
                        isDuplicate={invoiceData?.isDuplicate}
                        duplicates={invoiceData?.duplicates}
                        translationKey={"invoiceReview.tags.accounting"}
                      />
                      <div className="flex flex-col gap-8">
                        <InvoiceCoreInfoForm
                          reviewMode={reviewMode}
                          onTagUpdate={handleSaveInvoiceTags}
                        />
                        <div className="flex-1 flex flex-col gap-4">
                          {invoiceData?.invoice?.invoiceId && (
                            <InvoiceBookings
                              invoice={{
                                invoiceId: invoiceData.invoice.invoiceId,
                                isNettoInvoice: invoiceData.isNettoInvoice,
                                totalAmount: invoiceData.invoice.totalAmount,
                                currency: invoiceData.invoice.currency,
                                description: invoiceData.description,
                                invoiceText: invoiceData.invoiceText,
                              }}
                              onLineItemsChange={handleOnLineItemsChange}
                              onLoading={handleSetSyncingLineItems}
                            />
                          )}
                          {!invoiceReviewState?.invoiceData?.split &&
                            isPaymentEnabled && (
                              <>
                                <Separator className="my-2" />
                                <PaymentDetails />
                                <Separator className="my-2" />
                              </>
                            )}
                          {invoiceData?.invoiceId && showActivity && (
                            <SuperAdminOnly>
                              <InvoiceActivity
                                invoiceId={invoiceData?.invoiceId}
                              />
                            </SuperAdminOnly>
                          )}
                        </div>
                      </div>
                    </>
                  </TabsContent>
                  <TabsContent value="comments">
                    <InvoiceComments invoiceId={invoiceData?.invoiceId} />
                  </TabsContent>
                  <TabsContent value="ledger" className="flex-1 h-[500px]">
                    <LedgerEntriesTable
                      queryKeyPrefix="invoice-ledger"
                      tableName="invoice-ledger"
                      query={{
                        documentType: "invoice",
                        documentReference: [invoiceData?.invoiceId],
                      }}
                    />
                  </TabsContent>
                </Tabs>
              )}
          </div>
        </div>
      </div>
      <div id="line-item-bar-portal" className="shadow-top" />
      <div className="px-4 py-2">
        <NavigationBar
          disableConfirm={errors.length > 0 || syncingLineItems}
          disableSkip={syncingLineItems}
          disableBack={syncingLineItems}
          isDuplicate={invoiceData?.isDuplicate}
          onConfirmClick={handleConfirmClick}
          onDelete={() => handleInvoiceDelete(invoiceData?.invoiceId)}
          onRemoveDuplicates={handleRemoveDuplicates}
          isEditing={isEditing}
        />
      </div>
    </div>
  );
}

export default InvoiceReviewForm;
